import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {Alert, Button, Col, Input, message, Row, Space, Tooltip} from "antd";
import {
  CopyOutlined,
  FacebookFilled,
  MailFilled,
  TwitterOutlined,
} from "@ant-design/icons";
import {StyledServiceButtonWrapper} from "../../../../../styles/onboarding/onboarding";
import {useCreateSubscriptionContext} from "../../context/create-subscription";
import {
  useUpdateSubscriptionApi,
  type UpdateSubscriptionApiPayload,
} from "../../../../../react-query-hooks/store/subscriptions/update-subscription";
import {subscriptionCheckoutFormValidationSchema} from "../../schema-validation";
import {OfferingFormCheckoutFormValues} from "../../../../../components/offering-form/general/checkout-form/types";
import {SubscriptionCheckoutFormFields} from "../../../../../components/offering-form/subscriptions/checkout-form";
import {FormWrapper} from "../../../../../components/form-wrapper";
import {CustomModal} from "../../../../../components/modal";
import {REBRAND_BASE_URL} from "../../../../../services/config";
import {useGetProfileContext} from "../../../../../context/profile/profile-context";
import {StyledModalContentWrapper} from "../../../../../styles/transactions/transactions";
import {StyledSuccessServiceContent} from "../../../../../components/calls/service/service-form/service-form.style";
import {handleSubscriptionLink} from "../../../../../utils/products";
import {
  OFFERING_CREATE_DRAFT_KEYS,
  useClearOfferingCreateDraft,
  useSyncCheckoutFormToContext,
} from "../../../../../utils/offering-create-draft";

export function CreateSubscriptionCheckoutFormStep() {
  const navigate = useNavigate();
  const {state: profileState} = useGetProfileContext();
  const {
    state: {formData, apiResponseData},
    dispatch,
  } = useCreateSubscriptionContext();
  const updateSubscriptionMutation = useUpdateSubscriptionApi();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const clearSubscriptionDraft = useClearOfferingCreateDraft(
    OFFERING_CREATE_DRAFT_KEYS.subscription
  );

  const checkoutFormMethods = useForm<OfferingFormCheckoutFormValues>({
    resolver: yupResolver(subscriptionCheckoutFormValidationSchema),
    mode: "onChange",
    defaultValues: {
      bookingQuestions: formData.bookingQuestions ?? [],
    },
  });

  useSyncCheckoutFormToContext({
    checkoutFormMethods,
    dispatch,
    saveActionType: "save-create-subscription-form-values",
    formData: {
      bookingQuestions: formData.bookingQuestions ?? [],
    },
  });

  function onSubmit(values: OfferingFormCheckoutFormValues) {
    const subscriptionId = apiResponseData?.productSubscription?.id;
    if (!subscriptionId) {
      message.error("No subscription found. Please go back to step 1.");
      return;
    }

    dispatch({
      type: "save-create-subscription-form-values",
      payload: values,
    });

    const bookingQuestions =
      values.bookingQuestions
        ?.map((q) => (typeof q === "string" ? q : q.question))
        .filter(Boolean) ?? [];

    const updatePayload: UpdateSubscriptionApiPayload = {
      name: formData.name,
      description: formData.description,
      hideFromProfile: formData.hideFromProfile ?? false,
      bookingQuestions,
    };
    if (formData.isUrlRedirect && formData.url_redirect?.trim()) {
      updatePayload.redirectUrl = formData.url_redirect.trim();
    }

    updateSubscriptionMutation.mutate(
      {
        subscriptionId,
        payload: updatePayload,
      },
      {
        onSuccess: () => {
          setShowSuccessModal(true);
        },
      }
    );
  }

  const isFormError = Object.keys(checkoutFormMethods.formState.errors).length > 0;
  const isBookingQuestionsError = checkoutFormMethods.formState.errors.bookingQuestions;

  const handleSuccessModalOk = () => {
    setShowSuccessModal(false);
    clearSubscriptionDraft();
    navigate("/products?activeTab=subscriptions");
  };

  const subscriptionLink = handleSubscriptionLink({
    subscriptionId: apiResponseData?.productSubscription?.id ?? "",
    slug: apiResponseData?.productSubscription?.slug ?? "",
    username: profileState.profileData.username,
    tinyUrl: apiResponseData?.productSubscription?.tiny_url ?? "",
    windowNav: true,
  });

  return (
    <>
      <FormWrapper>
        <FormProvider {...checkoutFormMethods}>
          <form onSubmit={checkoutFormMethods.handleSubmit(onSubmit)} autoComplete="off">
            <Space size={16} direction="vertical" style={{display: "flex"}}>
              <SubscriptionCheckoutFormFields
                isBookingQuestionsError={!!isBookingQuestionsError}
              />
              {isFormError && (
                <Alert
                  message="Please fill in the required fields above, outlined in red"
                  type="warning"
                />
              )}
              <Row gutter={[0, 16]}>
                <Col span={24} sm={12}>
                  <StyledServiceButtonWrapper>
                    <Button
                      onClick={() => {
                        dispatch({
                          type: "save-create-subscription-form-values",
                          payload: checkoutFormMethods.getValues(),
                        });
                        dispatch({
                          type: "update-create-subscription-form-step",
                          payload: "automations",
                        });
                      }}
                      block
                      size="small"
                      type="default"
                    >
                      Back
                    </Button>
                    <Button
                      block
                      type="primary"
                      htmlType="submit"
                      size="small"
                      disabled={isFormError || !apiResponseData?.productSubscription?.id}
                      loading={updateSubscriptionMutation.isLoading}
                    >
                      Create subscription
                    </Button>
                  </StyledServiceButtonWrapper>
                </Col>
              </Row>
            </Space>
          </form>
        </FormProvider>
      </FormWrapper>
      {showSuccessModal && (
        <CustomModal
          visibleState={true}
          title="Subscription created"
          okFunc={handleSuccessModalOk}
          cancelFunc={handleSuccessModalOk}
          footerContent={[
            <Button
              key="publicProfile"
              type="primary"
              href={`${REBRAND_BASE_URL}/${profileState.profileData.username}`}
              target="_blank"
              size="small"
            >
              View my public profile
            </Button>,
            <Button
              size="small"
              key="gotIt"
              type="default"
              onClick={handleSuccessModalOk}
            >
              Got it!
            </Button>,
          ]}
        >
          <StyledModalContentWrapper>
            <StyledSuccessServiceContent>
              <p className="success-text">
                Your subscription has been successfully created! Share your subscription
                link to start making sales.
              </p>
              <div className="success-share-info">
                <Input.Group compact style={{display: "flex"}}>
                  <Input size="large" readOnly defaultValue={subscriptionLink} />
                  <Tooltip title="Copy subscription url">
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() =>
                        navigator.clipboard
                          .writeText(subscriptionLink)
                          .then(() => message.success("Copied"))
                      }
                    />
                  </Tooltip>
                </Input.Group>
                <p>or</p>
                <Space direction="horizontal" size={16}>
                  <div>
                    <Button
                      shape="circle"
                      size="small"
                      icon={<FacebookFilled />}
                      style={{
                        backgroundColor: "#EBEEF5",
                        color: "#3B5998",
                        border: "none",
                      }}
                      type="link"
                      target="_blank"
                      href={`https://www.facebook.com/sharer/sharer.php?u=${subscriptionLink}&amp`}
                      title="Share on facebook"
                    />
                  </div>
                  <div>
                    <Button
                      title="Share on twitter"
                      shape="circle"
                      size="small"
                      icon={<TwitterOutlined />}
                      target="_blank"
                      style={{
                        backgroundColor: "#E6F7FD",
                        color: "#00ACEE",
                        border: "none",
                      }}
                      href={`https://twitter.com/intent/tweet?text=I just created a subscription titled "${apiResponseData?.productSubscription?.name}" on Coachli. Purchase my subscription on Coachli!&url=${subscriptionLink}&hashtags=coachli,useCoachli`}
                    />
                  </div>
                  <div>
                    <Button
                      title="Send mail"
                      shape="circle"
                      size="small"
                      icon={<MailFilled />}
                      style={{
                        backgroundColor: "#FBECEB",
                        color: "#DB4437",
                        border: "none",
                      }}
                      href={`mailto:?subject=I wanted you to see this site&amp;body=I created a subscription ${subscriptionLink}`}
                    />
                  </div>
                </Space>
              </div>
            </StyledSuccessServiceContent>
          </StyledModalContentWrapper>
        </CustomModal>
      )}
    </>
  );
}
